import './OnboardingChildrenList.scss'

import { useFn } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import { type OnboardingUser } from '@op/services'
import cls from 'classnames'
import { memo, useMemo } from 'react'
import { useResponsiveMemo } from '../../../../../hooks'
import { Btn, Glyph } from '../../../../../widgets'
import type { SnapRowItemProps } from '../../../../../widgets/SnapRow/SnapRow'
import { SnapRow } from '../../../../../widgets/SnapRow/SnapRow'
import { OptionTile } from '../../../../../widgets/Tiles/OptionTile/OptionTile'
import { ChildProfilePreview } from '../../../Shared/ChildPreview/ChildProfilePreview'

type ChildrenListProps = {
	readonly childrenList: OnboardingUser[]
	readonly handleAddChild: () => void
	readonly selectedIdx: number
	readonly setSelectedIdx: (idx: number) => void
}

type SnapItem = OnboardingUser | 'add_child_btn'

const OnboardingChildrenList = ({
	childrenList,
	handleAddChild,
	selectedIdx,
	setSelectedIdx,
}: ChildrenListProps) => {
	const [t] = useT()

	const items = useMemo<SnapItem[]>(() => [...childrenList, 'add_child_btn'], [childrenList])

	const [mobileGap, mobileItemWidth] = useResponsiveMemo(({ fontScaled, matches }) => [
		fontScaled(matches.sm ? 16 : 12),
		fontScaled(matches.sm ? 135 : 110),
	])

	const renderItem = useFn(({ item, index }: SnapRowItemProps<SnapItem>) => (
		<OnboardingMobileTile
			active={selectedIdx === index}
			idx={index}
			item={item}
			onClick={item == 'add_child_btn' ? handleAddChild : setSelectedIdx}
		/>
	))

	return (
		<div className="onboarding-children">
			<SnapRow
				activeIndex={selectedIdx}
				align="center"
				className="onboarding-children__mobile"
				gap={mobileGap}
				items={items}
				itemWidth={mobileItemWidth}
				renderItem={renderItem}
				snap="mandatory"
			/>

			<div className="onboarding-children__desktop">
				{childrenList.map((user, idx) => (
					<OnboardingDesktopTile
						key={user.name}
						active={selectedIdx === idx}
						idx={idx}
						onClick={setSelectedIdx}
						user={user}
					/>
				))}

				<Btn
					className="w-full !justify-start gap-4 capitalize mt-3 !pl-9"
					invert
					onClick={handleAddChild}
				>
					<Glyph name="plus" size="sm" className="!font-bold" />
					<p className="capitalize">{t('add_child.button')}</p>
				</Btn>
			</div>
		</div>
	)
}

OnboardingChildrenList.displayName = 'OnboardingChildrenList'

export default memo(OnboardingChildrenList)

type OnboardingDesktopTileProps = {
	readonly active: boolean
	readonly idx: number
	readonly onClick: (idx: number) => void
	readonly user: OnboardingUser
}

const OnboardingDesktopTile = ({ active, idx, onClick, user }: OnboardingDesktopTileProps) => (
	<div className={cls('onboarding-tile', active && 'onboarding-tile--active')}>
		<OptionTile
			active={active}
			className={cls('onboarding-tile__content', active && 'onboarding-tile__content--active')}
			onClick={() => onClick(idx)}
		>
			<ChildProfilePreview
				idx={idx}
				age={`Age ${user.age}`}
				className="child-list-preview"
				name={user.name}
			/>
		</OptionTile>
	</div>
)

type OnboardingMobileTileProps = {
	readonly active: boolean
	readonly idx: number
	readonly item: SnapItem
	readonly onClick: (idx: number) => void
}

const OnboardingMobileTile = ({ active, idx, item, onClick }: OnboardingMobileTileProps) => {
	const [t] = useT()
	const isAddButton = item == 'add_child_btn'

	return (
		<div className={cls('onboarding-tile', active && !isAddButton && 'onboarding-tile--active')}>
			<OptionTile
				onClick={() => onClick(idx)}
				active={active}
				className={cls(
					'onboarding-tile__content',
					active && 'onboarding-tile__content--active',
					isAddButton && 'onboarding-tile__content--add-btn',
				)}
			>
				{isAddButton ?
					<div className="flex flex-col">
						<Glyph name="plus" className="text-teal-2dk" />
						<p className="font-semibold text-teal-2dk">{t('add_child.button')}</p>
					</div>
				:	<ChildProfilePreview
						key={item.name}
						idx={idx}
						name={item.name}
						age={`Age ${item.age}`}
						direction="vertical"
						className="w-full select-none"
					/>
				}
			</OptionTile>
		</div>
	)
}
